import {
  ReactNode,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Autocomplete,
  AutocompleteProps,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import scssVariables from '@styles/variables.module.scss';
import { useStore } from '@tanstack/react-form';
import {
  InfiniteData,
  InfiniteQueryObserverResult,
  keepPreviousData,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { SelectInputCreateItemOption } from './components/SelectInputCreateItemOption';
import styles from './SelectInputField.module.scss';
import { EndAdornmentWithStatusIcon } from '../../utils/EndAdornmentWithStatusIcon';
import { useGetTypeSenseAPIKeys } from '@/api/REST/typeSenseAPIKeys';
import { TYPESENSE_SELECT_OPTIONS_WINDOW_SIZE } from '@/api/typesense/constants';
import type {
  SelectOptionSearchFunction,
  SearchOptions,
  TypesenseCollectionConfig,
  TypesenseCollectionName,
  TypesenseKeyName,
  TypesenseCollectionApiKeyPair,
  GetSelectOptionByIdFunction,
  GetSelectOptionByIdFromUnionFunction,
  SelectOptionFromUnionSearchFunction,
  UnionSearchOptions,
} from '@/api/typesense/types';
import { FullTextTooltip } from '@/components/ui/FullTextTooltip';
import { InfoIconWithTooltip } from '@/components/ui/InfoIconWithTooltip';
import { useFieldContext } from '@/forms/formContext';
import { BaseInputFieldProps, BaseSelectOption } from '@/forms/types';

interface BaseSelectInputFieldProps
  extends
    Omit<BaseInputFieldProps, 'placeholder'>,
    Omit<
      SelectProps,
      'value' | 'onChange' | 'onBlur' | 'name' | 'id' | 'label' | 'required'
    > {
  dependsOnFields?: never;
}

interface BaseAutocompleteSelectInputFieldProps
  extends
    Omit<BaseInputFieldProps, 'placeholder'>,
    Omit<
      AutocompleteProps<BaseSelectOption, false, false, false>,
      | 'value'
      | 'onChange'
      | 'onBlur'
      | 'name'
      | 'id'
      | 'label'
      | 'required'
      | 'options'
      | 'renderInput'
    > {
  endAdornment?: ReactNode;
  /**
   * Sibling fields whose values scope the available options in this field. If a listed field
   * changes from its default, the select field resets when its current value is no longer valid.
   * On edit forms, reset is skipped while all as dependent listed fields (and the field itself) still hold their
   * original default values. Pass short field names (e.g. `'customer'`); prefix with the group
   * for cross-group dependencies (e.g. `'other_group.customer'`).
   */
  dependsOnFields?: string[];
}

/**
 * Regular select props: not connected to Typesense internally, not searchable, options are passed in directly as a prop
 */
interface RegularSelectProps extends BaseSelectInputFieldProps {
  options: Array<BaseSelectOption>;
  searchFunction?: never;
  searchOptions?: never;
  getOptionByIdFallbackFunction?: never;
  typeSenseCollection?: never;
  onCreateNewOptionClick?: () => void;
  helperText?: string | ReactNode;
}

/**
 * Autocomplete select props — single collection variant
 */
interface SingleCollectionAutocompleteSelectProps extends BaseAutocompleteSelectInputFieldProps {
  typeSenseCollection: TypesenseCollectionConfig;
  searchFunction: SelectOptionSearchFunction;
  getOptionByIdFallbackFunction: GetSelectOptionByIdFunction;
  searchOptions?: SearchOptions;
  options?: never;
  onCreateNewOptionClick?: (newOptionName?: string) => void;
  helperText?: string | ReactNode;
}

/**
 * Autocomplete select props — union multi-search variant
 * Use when options list is fetched with a union search to typesense,
 * either from different collections or the same collection with different scoped API keys.
 */
interface UnionCollectionAutocompleteSelectProps extends BaseAutocompleteSelectInputFieldProps {
  typeSenseCollection: TypesenseCollectionConfig[];
  searchFunction: SelectOptionFromUnionSearchFunction;
  getOptionByIdFallbackFunction: GetSelectOptionByIdFromUnionFunction;
  searchOptions?: SearchOptions | UnionSearchOptions;
  options?: never;
  onCreateNewOptionClick?: (newOptionName?: string) => void;
  helperText?: string | ReactNode;
}

export function SelectInputField({
  required,
  label,
  showIsValid = false,
  options,
  infoTooltip,
  disabledTooltip,
  searchFunction,
  searchOptions,
  getOptionByIdFallbackFunction,
  typeSenseCollection,
  onCreateNewOptionClick,
  helperText,
  dependsOnFields,
  ...props
}:
  | RegularSelectProps
  | SingleCollectionAutocompleteSelectProps
  | UnionCollectionAutocompleteSelectProps) {
  const field = useFieldContext();

  const isSearchableSelect = !!searchFunction && !!typeSenseCollection;
  const hasCreateNewOptionButton = !!onCreateNewOptionClick;

  useEffect(() => {
    // Auto-reset field value if current value is not in options for regular select
    if (!isSearchableSelect) {
      const currentValue = field.state.value as BaseSelectOption | undefined;

      if (currentValue?.id) {
        const isValueInOptions = options?.some(
          (option) => option && option.id === currentValue.id,
        );

        if (!isValueInOptions) {
          field.handleChange(null);
        }
      }
    }
  }, [options, field.state.value, isSearchableSelect]);

  const autocompleteRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const [isListboxOpen, setIsListboxOpen] = useState(false);

  const [searchInput, setSearchInput] = useState('');

  // used to search error persistent in the UI to not disappear on blur
  const [isSearchError, setIsSearchError] = useState<boolean>(false);

  // Do not use form state directly as this can cause the reactivity to break in some cases
  const submissionAttempts = useStore(
    field.form.store,
    (state) => state.submissionAttempts,
  );

  /*
   * parse out group prefix from field name when existing to be able to resolve dependencies within
   * the same group based on short field names
   */
  const fieldGroupPrefix = field.name.includes('.')
    ? field.name.substring(0, field.name.lastIndexOf('.'))
    : '';

  const resolvedDependsOnFields = dependsOnFields?.map((name) =>
    !name.includes('.') && fieldGroupPrefix
      ? `${fieldGroupPrefix}.${name}`
      : name,
  );

  // get the meta states of the fields the current field depends on
  const dependsOnFieldsMeta = useStore(
    field.form.store,
    (state) =>
      resolvedDependsOnFields?.map((name) => state.fieldMeta[name]) ?? [],
  );

  const isValid = !!(field.state.meta.isValid && field.state.meta.isDirty);
  const isFormError = !!(
    (!field.state.meta.isValid && field.state.meta.isDirty) ||
    (!field.state.meta.isValid && submissionAttempts > 0)
  );

  const { t } = useTranslation('forms');

  const infoTooltipId = infoTooltip ? `id_${field.name}_tooltip` : undefined;
  const helperTextId =
    helperText && !isFormError && !isSearchError
      ? `id_${field.name}_helper`
      : undefined;
  const displayFormError = isFormError ? field.state.meta.errors[0] : null;
  const errorId =
    displayFormError || isSearchError ? `id_${field.name}_error` : null;
  const describedByIds = [infoTooltipId, helperTextId, errorId]
    .filter(Boolean)
    .join(' ');

  const handleSearch = (value: string, reason: string) => {
    if (value === '') {
      setSearchInput('');
      return;
    }

    if (reason === 'input') {
      setIsSearchError(false);
      setSearchInput(value);
    }
  };

  // reason is only set when using Autocomplete
  const handleChange = (id: string, reason?: string) => {
    setIsSearchError(false);

    if (searchInput) {
      // reset search to show full options list with selected option
      setSearchInput('');
    }

    if (id === 'create_new_option') {
      handleOnCreateNewOptionClicked();
      return;
    }

    if (reason && reason === 'clear') {
      field.handleChange(null);
      return;
    }

    const _options = isSearchableSelect ? flattenedPaginatedOptions : options;
    const selectedOption = _options?.find(
      (option) => option && option.id === id,
    );

    if (!selectedOption) {
      field.handleChange(null);
      return;
    }

    field.handleChange(selectedOption);
  };

  const handleOnCreateNewOptionClicked = () => {
    if (onCreateNewOptionClick) {
      onCreateNewOptionClick(searchInput.trim() || '');
    }

    // programmatically blur the input to close the dropdown
    if (autocompleteRef.current) {
      autocompleteRef.current.querySelector('input')?.blur();
    }
  };

  const handleClose = () => {
    setSearchInput('');
    setIsListboxOpen(false);
  };

  const { data: apiKeys } = useGetTypeSenseAPIKeys();
  const isUnionMultiSearch = Array.isArray(typeSenseCollection);

  const collectionApiKey =
    apiKeys && typeSenseCollection
      ? !isUnionMultiSearch
        ? (apiKeys[(typeSenseCollection as TypesenseCollectionConfig).keyName]
            ?.key ?? null)
        : (() => {
            const pairs = (typeSenseCollection as TypesenseCollectionConfig[])
              .map((collection) => ({
                collectionName: collection.name as TypesenseCollectionName,
                keyName: collection.keyName as TypesenseKeyName,
                apiKey: apiKeys[collection.keyName]?.key,
              }))
              .filter(
                (pair): pair is TypesenseCollectionApiKeyPair => !!pair.apiKey,
              );
            return pairs.length > 0 ? pairs : null;
          })()
      : null;

  const currentQueryKey = [
    ...(!isUnionMultiSearch
      ? [
          typeSenseCollection?.name,
          // we need the keyName here as well to be able to distinct between different queries using the same collection but different keys when invalidating etc.
          typeSenseCollection?.keyName,
          collectionApiKey,
        ]
      : [
          ...typeSenseCollection.map((collection) => collection.name),
          ...typeSenseCollection.map((collection) => collection.keyName),
          ...(collectionApiKey
            ? (collectionApiKey as TypesenseCollectionApiKeyPair[]).map(
                (x) => x.apiKey,
              )
            : []),
        ]),
    searchInput,
    searchFunction,
    searchOptions,
  ];

  const {
    data: paginatedData,
    isLoading: isSearchLoading,
    error: searchError,
    fetchNextPage,
    hasNextPage,
    isFetching,
    status,
  } = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: currentQueryKey,
    queryFn: ({ pageParam }) =>
      searchFunction
        ? !isUnionMultiSearch
          ? (searchFunction as SelectOptionSearchFunction)(
              searchInput,
              collectionApiKey! as string,
              pageParam,
              searchOptions as SearchOptions,
            )
          : (searchFunction as SelectOptionFromUnionSearchFunction)(
              searchInput,
              collectionApiKey as TypesenseCollectionApiKeyPair[],
              pageParam,
              searchOptions,
            )
        : undefined,
    enabled: isSearchableSelect && !!collectionApiKey && !!searchFunction,
    staleTime: searchInput === '' ? 15000 : 0, // keep base query fresh long enough for Typesense to index newly created options before a refetch overwrites them
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (lastPage && lastPage.length < TYPESENSE_SELECT_OPTIONS_WINDOW_SIZE) {
        return undefined;
      }
      return lastPageParam + 1;
    },
    placeholderData: keepPreviousData,
  });

  const flattenedPaginatedOptions = useMemo(() => {
    if (status === 'error') {
      return [];
    }
    return paginatedData?.pages?.flatMap((page) => (page ? page : [])) ?? [];
  }, [paginatedData, status]);

  const scrollToSelectedOptionInList = () => {
    setTimeout(() => {
      // find the selected list item and scroll to it if it exists
      const selectedListItem = listboxRef?.current?.querySelector(
        `li[aria-selected="true"]`,
      ) as HTMLElement;
      if (selectedListItem) {
        selectedListItem.scrollIntoView({
          behavior: 'auto',
          block: 'center',
        });
      }
    }, 0);
  };

  // Manually scroll to selected option when dropdown opens
  useEffect(() => {
    if (isListboxOpen && field.state.value && listboxRef.current) {
      scrollToSelectedOptionInList();
    }
  }, [isListboxOpen, field.state.value, listboxRef]);

  const fetchListOptionsIfNeeded = async () => {
    const currentSelectValue = field?.state?.value as BaseSelectOption;
    if (currentSelectValue) {
      if (collectionApiKey && getOptionByIdFallbackFunction) {
        // Check if the selected value exists in the options based on the ID first
        let optionById: BaseSelectOption | null = null;

        /*
         * On edit forms, we need to skip the existing check resets, as long as the field itself is the default value
         * or all the fields it depends on are default values, as this means the specific fields are still the same as the saved db entry
         * which was valid eventually when it was saved last time
         *
         * Note: Potentially this needs to move out of the function, so we can use it as a dependecy in the useEffects
         * to trigger the reset right away when this becomes true and not only on the next opening/ other trigger
         * */
        const shouldResetOnNoOptionByIdFoundWithCurrentFilters =
          !field.state.meta.isDefaultValue ||
          dependsOnFieldsMeta.some((meta) => meta?.isDefaultValue === false);

        try {
          optionById = !isUnionMultiSearch
            ? await (
                getOptionByIdFallbackFunction as GetSelectOptionByIdFunction
              )(
                currentSelectValue.id,
                collectionApiKey as string,
                (searchOptions as SearchOptions)?.filterBy,
              )
            : await (
                getOptionByIdFallbackFunction as GetSelectOptionByIdFromUnionFunction
              )(
                currentSelectValue.id,
                collectionApiKey as TypesenseCollectionApiKeyPair[],
                searchOptions,
              );
        } catch {
          if (shouldResetOnNoOptionByIdFoundWithCurrentFilters) {
            handleChange('', 'clear');
          }
          return;
        }

        if (!optionById && shouldResetOnNoOptionByIdFoundWithCurrentFilters) {
          handleChange('', 'clear');
          return;
        }

        /*
         * If we find a valid option, fetch pages until we find it or run out of pages so we can scroll
         * to the correct position in the dropdown when list box is opened
         */

        if (!isListboxOpen) {
          return;
        }

        let lastResult: InfiniteQueryObserverResult<
          InfiniteData<BaseSelectOption[] | undefined, unknown>
        >;
        let preselectedOption: BaseSelectOption | undefined;
        let _hasNextPage = hasNextPage ?? false;

        preselectedOption = flattenedPaginatedOptions?.find(
          (option) =>
            option &&
            option.id === (field?.state?.value as BaseSelectOption)?.id,
        );

        while (!preselectedOption && _hasNextPage) {
          lastResult = await fetchNextPage();
          preselectedOption = lastResult?.data?.pages[
            lastResult.data?.pages.length - 1
          ]?.find(
            (option) =>
              option &&
              option.id === (field?.state?.value as BaseSelectOption)?.id,
          );

          _hasNextPage = lastResult?.hasNextPage ?? false;
        }

        // scroll to selected element, which should now be in the list
        if (preselectedOption) {
          scrollToSelectedOptionInList();
        }
      }
    }
  };

  // Fetch option list until pre-selected value is found to scroll to selected option in list
  useEffect(() => {
    if (isListboxOpen && searchInput === '') {
      fetchListOptionsIfNeeded();
    }
  }, [searchInput, isListboxOpen]);

  const searchOptionsString = JSON.stringify(searchOptions);

  // when search options change (e.g. filterBy), refetch options if we have a value selected to reset option if needed (selection outside of new list)
  useEffect(() => {
    if (!!searchOptionsString && searchOptionsString !== '{}' && !isFetching) {
      fetchListOptionsIfNeeded();
    }
  }, [searchOptionsString, isFetching]);

  useEffect(() => {
    if (isSearchableSelect && searchError && !isSearchError) {
      setIsSearchError(true);
      console.error('Error searching options:', field.name, searchError);
    }
  }, [isSearchableSelect, searchError, isSearchError]);

  const selectInputField = isSearchableSelect ? (
    <Autocomplete
      {...(props as BaseAutocompleteSelectInputFieldProps)}
      ref={autocompleteRef}
      options={[
        ...flattenedPaginatedOptions,
        ...(hasCreateNewOptionButton
          ? [
              {
                id: 'create_new_option',
                value: '',
              },
            ]
          : []),
      ]}
      renderInput={(params) => (
        <TextField
          {...params}
          error={isFormError || isSearchError}
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              id: `id_${field.name}`,
              ...(describedByIds && {
                'aria-describedby': describedByIds,
              }),
            },
            input: {
              ...params.InputProps,
              ...(((showIsValid && isValid) ||
                isFormError ||
                isSearchError ||
                props.endAdornment ||
                params.InputProps.endAdornment) && {
                endAdornment: (
                  <EndAdornmentWithStatusIcon
                    isValid={showIsValid && isValid}
                    isError={isFormError || isSearchError}
                    endAdornment={
                      <>
                        {props.endAdornment}
                        {params.InputProps.endAdornment}
                      </>
                    }
                  />
                ),
              }),
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        if (!option) {
          return;
        }

        if (option.id === 'create_new_option') {
          return (
            <>
              {flattenedPaginatedOptions.length === 0 && (
                <div className={styles.CustomNoOptionsText} role="presentation">
                  {t('select.EmptyOptionMenuText')}
                </div>
              )}
              <SelectInputCreateItemOption
                data-automation-id={`${field.name}-select-input-create-item-option`}
                props={{ ...props, role: 'option' }}
                key="create_new_option"
                className="MuiAutocomplete-option" // to match other options' styling
                currentSearchInput={searchInput}
                onClick={handleOnCreateNewOptionClicked}
              />
            </>
          );
        }

        return (
          <li {...props} key={option.id}>
            {option.value}
          </li>
        );
      }}
      getOptionLabel={(option) => option?.value ?? ''}
      getOptionKey={(option) => option?.id ?? `null-${Math.random()}`}
      isOptionEqualToValue={(option, value) =>
        (option && value && option?.id === value?.id) ?? false
      }
      onChange={(_event, value, reason) =>
        handleChange(value?.id as string, reason)
      }
      onInputChange={(_event, value, reason) => handleSearch(value, reason)}
      value={(field.state.value as BaseSelectOption) ?? null}
      filterOptions={(x) => x} // disable built-in filtering to rely on server-side filtering
      onOpen={(_event: SyntheticEvent) => {
        setIsListboxOpen(true);
      }}
      onClose={handleClose}
      slotProps={{
        listbox: {
          ref: listboxRef,
          onScroll: (event: SyntheticEvent) => {
            const listboxNode = event.currentTarget;
            const scrollTop = listboxNode.scrollTop;
            const scrollHeight = listboxNode.scrollHeight;
            const clientHeight = listboxNode.clientHeight;

            if (
              scrollTop + clientHeight >= scrollHeight - 1 &&
              hasNextPage &&
              !isFetching
            ) {
              fetchNextPage();
            }
          },
        },
      }}
      loading={isSearchLoading}
      loadingText={<Skeleton variant="rounded" width="100%" height={12} />}
      noOptionsText={
        // use this slot for error display as well
        isSearchError ? (
          <Typography color={scssVariables.ColorCoral80} variant="label">
            {t('select.SearchErrorText')}
          </Typography>
        ) : (
          t('select.EmptyOptionMenuText')
        )
      }
    />
  ) : (
    <Select
      {...(isSearchableSelect ? {} : (props as BaseSelectInputFieldProps))}
      name={field.name}
      value={(field.state.value as BaseSelectOption)?.id ?? ''}
      onBlur={field.handleBlur}
      onChange={(e) => handleChange(e.target.value as string)}
      inputProps={{
        ...(describedByIds && {
          'aria-describedby': describedByIds,
        }),
      }}
      labelId={`id_${field.name}_label`}
      {...(((showIsValid && isValid) ||
        isFormError ||
        isSearchError ||
        props.endAdornment) && {
        endAdornment: (
          <EndAdornmentWithStatusIcon
            isValid={showIsValid && isValid}
            isError={isFormError || isSearchError}
            endAdornment={props.endAdornment}
          />
        ),
      })}
    >
      {options && options.length > 0 ? (
        options.map((option) => {
          return (
            !!option && (
              <MenuItem key={option.id} value={option.id}>
                {option.value}
              </MenuItem>
            )
          );
        })
      ) : (
        <MenuItem disabled>{t('select.EmptyOptionMenuText')}</MenuItem>
      )}
      {hasCreateNewOptionButton && (
        <SelectInputCreateItemOption onClick={onCreateNewOptionClick} />
      )}
    </Select>
  );

  return (
    <FormControl
      valid={showIsValid && isValid}
      error={isFormError || isSearchError}
    >
      <Stack direction="row">
        <FullTextTooltip>
          <InputLabel
            required={required}
            id={`id_${field.name}_label`}
            {...(isSearchableSelect && { htmlFor: `id_${field.name}` })}
          >
            {label}
          </InputLabel>
        </FullTextTooltip>
        {infoTooltip && infoTooltipId && (
          <InfoIconWithTooltip id={infoTooltipId} tooltip={infoTooltip} />
        )}
      </Stack>

      {props.disabled && disabledTooltip ? (
        <Tooltip title={disabledTooltip}>{selectInputField}</Tooltip>
      ) : (
        selectInputField
      )}

      {isFormError && displayFormError && errorId ? (
        <FormHelperText error id={errorId}>
          {displayFormError.message?.startsWith('validationErrors.')
            ? t(displayFormError.message, {
                value: label ?? t('validationErrors.DefaultValueLabel'),
              })
            : displayFormError.message}
        </FormHelperText>
      ) : null}
      {isSearchError && errorId ? (
        <FormHelperText error id={errorId}>
          {t('select.SearchErrorText')}
        </FormHelperText>
      ) : null}
      {helperText && !isFormError && !isSearchError ? (
        <FormHelperText
          id={helperTextId}
          component={typeof helperText === 'string' ? 'p' : 'div'}
        >
          {helperText}
        </FormHelperText>
      ) : null}
    </FormControl>
  );
}
